import {
  Directive,
  effect,
  ElementRef,
  inject,
  InjectionToken,
  input,
  InputSignal,
  Provider,
  signal,
  Signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { deepMergeMultiple } from 'app/core/utils/deep-merge';
import { DeepPartial } from 'app/core/utils/deep-partial';
import { PartialDeep } from 'app/core/utils/partial-deep';
import { PartialWithRequired } from 'app/core/utils/partial-with-required';
import { EMPTY, fromEvent, map, switchMap } from 'rxjs';
import { findClosestPrecedingHeader } from '../find-closest-preceding-header';
import { fromMutationObserver } from './fromMutationObserver';
import { GtmEvent, GtmEventInfo, GtmPushEvent, ShebaGTMService } from './gtm.service';
import { resolveSignalProperty } from './resolve-signal-property';
import { PickSignalFactory } from './SignalFactory';
import { CURRENT_TEMPLATE } from './use-current-template';

// --- GtmEvent ---

/**
 * Stores the Tree of GTM events from the top level to the current level.
 * This is used to provide in root component GTM events params such as `event` and `eventInfo.template`
 */
export const ShebaGtmEvents = new InjectionToken<
  PartialDeep<{
    event: GtmEvent;
    eventInfo: GtmEventInfo;
  }>[]
>('ShebaGtmEvent');

/**
 * used to provide in root component GTM events params such as `event` and `eventInfo.template`
 *
 * TODO: May be change it to Host Directive?
 */
export const provideGtmEvent = (
  event: PartialDeep<{
    event: GtmEvent;
    eventInfo: PickSignalFactory<GtmEventInfo, 'medical_domain' | 'template' | 'medical_department' | 'institue_name'>;
  }>
): Provider => ({
  provide: ShebaGtmEvents,
  useFactory: () => {
    resolveSignalProperty(event.eventInfo, 'medical_domain');
    resolveSignalProperty(event.eventInfo, 'template');
    resolveSignalProperty(event.eventInfo, 'medical_department');
    resolveSignalProperty(event.eventInfo, 'institue_name');

    return [event];
  }
});

/**
 * gets the current GTM events from the root component
 */
export function useCurrentGtmEvents(): DeepPartial<{
  event: GtmEvent;
  eventInfo: GtmEventInfo;
}> {
  const events = inject(ShebaGtmEvents, { optional: true }) ?? [];

  return deepMergeMultiple<GtmPushEvent>(...events);
}

/**
 * use to set GTM event in the current ShebaGtmEvents provider context.
 */
export function useSetGtmEvent() {
  const event = signal<EventOrEventMap<PartialDeep<GtmPushEvent>> | undefined>(undefined);

  const events = inject(ShebaGtmEvents);

  effect(
    () => {
      const lastEvent = events[events.length - 1];
      events[events.length - 1] = deepMergeMultiple(lastEvent, event() ?? {}) as GtmPushEvent;
    },
    { allowSignalWrites: true }
  );

  return {
    event
  };
}

/**
 * Using to provide GTM event in the component.
 */
@Directive({
  selector: '[shebaGtmEvent]',
  standalone: true,
  providers: [
    {
      provide: ShebaGtmEvents,
      useValue: []
    }
  ]
})
export class ShebaGtmEventDirective {
  /**
   * Can be a key-value map, but `eventInfo.template` or `event` should be defined in the `root`.
   * ```typescript
   * {
   *    'default': { event: 'homepage', eventInfo: { user_action: 'click on {{text}}', template: 't5' } },
   *    't1': { event: 'article', eventInfo: { user_action: 'click on {{text}}', template: 't5' } },
   *    't2': { event: 'article', eventInfo: { user_action: 'home page click', template: 't5' } }
   * }
   * ```
   */
  readonly event = input.required<EventOrEventMap<PartialDeep<GtmPushEvent>>>({
    alias: 'shebaGtmEvent'
  });

  constructor() {
    const parent = inject(ShebaGtmEvents, { optional: true, skipSelf: true }) ?? [];
    const self = inject(ShebaGtmEvents, { self: true });

    effect(
      () => {
        self.length = 0;

        const { mergedParents, currentEvent } = getEvent({ parent, event: this.event });
        const newSelfEvent = deepMergeMultiple(mergedParents, currentEvent);
        self.push(newSelfEvent);
      },
      { allowSignalWrites: true }
    );
  }
}

// --- service ---

/**
 * Used to pushEvent directly to the GTM service.
 * Will get additional information from injected `ShebaGtmEvents` if available.
 */
export function useShebaGtmService(props: { element?: Signal<HTMLElement> } = {}) {
  const gtmService = inject(ShebaGTMService);
  const parent = inject(ShebaGtmEvents, { optional: true }) ?? [];
  const elementRef = inject(ElementRef, { optional: true });
  const currentTemplate = inject(CURRENT_TEMPLATE, { optional: true });

  return {
    pushEvent(event: PartialDeep<GtmPushEvent>) {
      const data = {
        eventInfo: {
          template: currentTemplate?.(),
          screen_name: window.location.pathname,
          header: findClosestPrecedingHeader(props.element?.() || elementRef?.nativeElement)
        }
      };
      gtmService.push(deepMergeMultiple<GtmPushEvent>(data, ...parent, event));
    }
  };
}

// --- user_click ---

/**
 * Directive to track user clicks on buttons or links.
 * It pushes the click event to the GTM service with the provided eventInfo.
 */
@Directive({
  selector: 'button[shebaGtmClick], a[shebaGtmClick], [shebaGtmClick]',
  standalone: true,
  host: {
    '(click)': 'clickHandler($event)'
  }
})
export class ShebaGtmClickDirective {
  readonly #gtmService = inject(ShebaGTMService);
  readonly #element = inject<ElementRef<HTMLButtonElement | HTMLAnchorElement>>(ElementRef).nativeElement;
  readonly #parent = inject(ShebaGtmEvents, { optional: true }) ?? [];
  readonly #currentTemplate = inject(CURRENT_TEMPLATE, { optional: true });

  /**
   * Can be a key-value map, but `eventInfo.template` or `event` should be defined in the `root`.
   * ```typescript
   * {
   *    'default': { user_action: 'click on {{text}}' },
   *    't1': { user_action: 'click on {{text}}' },
   *    't2': { user_action: 'home page click' }
   * }
   * ```
   */
  readonly eventInfo = input.required<EventOrEventMap<PartialWithRequired<GtmEventInfo, 'user_action'>>>({
    alias: 'shebaGtmClick'
  });

  clickHandler(e: PointerEvent) {
    const { mergedParents: parent, currentEvent: self } = getEventInfo({
      parent: this.#parent,
      eventInfo: this.eventInfo
    });

    const data = {
      eventInfo: {
        screen_name: window.location.pathname,
        click_text: this.#element?.innerText ?? null,
        header: findClosestPrecedingHeader(this.#element),
        template: this.#currentTemplate?.()
      }
    };

    const gtmClickEvent = deepMergeMultiple<GtmPushEvent>(data, parent, self);
    this.#gtmService.push(gtmClickEvent);
  }
}

// --- user_search ---

/**
 * Directive to track search form submissions.
 * It pushes the search event to the GTM service with the provided eventInfo.
 */
@Directive({
  selector: 'form[shebaGtmSearch]',
  standalone: true,
  host: {
    '(submit)': 'submitHandler($event)'
  }
})
export class ShebaGtmSearchDirective {
  readonly #gtmService = inject(ShebaGTMService);
  readonly #element = inject<ElementRef<HTMLFormElement>>(ElementRef).nativeElement;
  readonly #parent = inject(ShebaGtmEvents, { optional: true }) ?? [];
  readonly #currentTemplate = inject(CURRENT_TEMPLATE, { optional: true });

  readonly eventInfo = input.required<EventOrEventMap<PartialWithRequired<GtmEventInfo, 'user_action'>>>({
    alias: 'shebaGtmSearch'
  });

  submitHandler(e: SubmitEvent) {
    const { mergedParents: parent, currentEvent: self } = getEventInfo({
      parent: this.#parent,
      eventInfo: this.eventInfo
    });

    const searchInput = this.#element.elements.namedItem('search') as HTMLInputElement;
    const data = {
      eventInfo: {
        screen_name: window.location.pathname,
        user_action: self.eventInfo.user_action?.replace('{{search_term}}', searchInput?.value ?? '') ?? null,
        click_text: e.submitter?.innerText ?? null,
        search_term: searchInput?.value ?? null,
        header: findClosestPrecedingHeader(this.#element),
        template: this.#currentTemplate?.()
      }
    };
    this.#gtmService.push(deepMergeMultiple<GtmPushEvent>(data, parent, self));
  }
}

@Directive({
  selector: '[shebaGtmInnerHTMLClick]',
  standalone: true
})
export class ShebaGtmInnerHTMLClickDirective {
  readonly #gtmService = inject(ShebaGTMService);
  readonly #element = inject<ElementRef<HTMLButtonElement | HTMLAnchorElement>>(ElementRef).nativeElement;
  readonly #parent = inject(ShebaGtmEvents, { optional: true }) ?? [];
  readonly #currentTemplate = inject(CURRENT_TEMPLATE, { optional: true });

  readonly #button$ = fromMutationObserver(this.#element, { childList: true }).pipe(
    map(() => this.#element.querySelector('button, a'))
  );
  readonly #clicks$ = this.#button$.pipe(
    switchMap((button) => (button ? fromEvent<PointerEvent>(button, 'click') : EMPTY))
  );

  // readonly #button =
  constructor() {
    this.#clicks$.pipe(takeUntilDestroyed()).subscribe((event) => {
      this.clickHandler(event);
    });
  }

  /**
   * Can be a key-value map, but `eventInfo.template` or `event` should be defined in the `root`.
   * ```typescript
   * {
   *    'default': { user_action: 'click on {{text}}' },
   *    't1': { user_action: 'click on {{text}}' },
   *    't2': { user_action: 'home page click' }
   * }
   * ```
   */
  readonly eventInfo = input.required<EventOrEventMap<PartialWithRequired<GtmEventInfo, 'user_action'>>>({
    alias: 'shebaGtmInnerHTMLClick'
  });

  clickHandler(e: PointerEvent) {
    const { mergedParents: parent, currentEvent: self } = getEventInfo({
      parent: this.#parent,
      eventInfo: this.eventInfo
    });

    const data = {
      eventInfo: {
        screen_name: window.location.pathname,
        click_text: this.#element?.innerText ?? null,
        header: findClosestPrecedingHeader(this.#element),
        template: this.#currentTemplate?.()
      }
    };

    const gtmClickEvent = deepMergeMultiple<GtmPushEvent>(data, parent, self);
    this.#gtmService.push(gtmClickEvent);
  }
}

/**
 * Module to provide GTM directives.
 *
 * Use this instead of importing each directive separately.
 *
 * It includes `ShebaGtmSearchDirective`, `ShebaGtmClickDirective`, `ShebaGtmEventDirective`.
 */
export const ShebaGtmModule = [
  ShebaGtmSearchDirective,
  ShebaGtmClickDirective,
  ShebaGtmEventDirective,
  ShebaGtmInnerHTMLClickDirective
];

/**
 * Can be a key-value map, but `eventInfo.template` or `event` should be defined in the `root`.
 * ```typescript
 * {
 *    'default': { user_action: 'click on {{text}}' },
 *    't1': { user_action: 'click on {{text}}' },
 *    't2': { user_action: 'home page click' }
 * }
 * ```
 */
function getEventInfo(props: {
  parent: PartialDeep<{
    event: GtmEvent;
    eventInfo: GtmEventInfo;
  }>[];
  eventInfo: InputSignal<EventOrEventMap<PartialWithRequired<GtmEventInfo, 'user_action'>>>;
}) {
  const mergedParents = deepMergeMultiple<GtmPushEvent>(...props.parent);

  const template = mergedParents?.eventInfo?.template ?? 'default';

  const currentEventInfo = untracked(props.eventInfo);

  const currentEvent = {
    eventInfo: isEventMap(currentEventInfo)
      ? (currentEventInfo as any)[template] ?? currentEventInfo['default']
      : currentEventInfo
  };

  return { mergedParents, currentEvent };
}

function getEvent(props: {
  parent: PartialDeep<{
    event: GtmEvent;
    eventInfo: Partial<GtmEventInfo>;
  }>[];
  event: InputSignal<EventOrEventMap<PartialDeep<GtmPushEvent>>>;
}) {
  const mergedParents = deepMergeMultiple<GtmPushEvent>(...props.parent);

  const template = mergedParents?.eventInfo?.template ?? 'default';

  const currentEventOrEventMap = untracked(props.event);

  const currentEvent = isEventMap(currentEventOrEventMap)
    ? (currentEventOrEventMap as any)[template] ?? currentEventOrEventMap['default']
    : currentEventOrEventMap;

  return { mergedParents, currentEvent };
}

function isEventMap<TEvent>(value: unknown): value is EventMap<TEvent> {
  return typeof value === 'object' && value !== null && 'default' in value;
}

type EventOrEventMap<TEvent> = TEvent | EventMap<TEvent>;

type EventMap<TEvent> = {
  default: TEvent;
  [key: `t${number}`]: TEvent;
};
